import Layout from 'components/layout/Layout'
import ProducerDetail from 'features/producer-detail/ProducerDetail'
import type { ProducerDetailProps } from 'features/producer-detail/types'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { Language } from 'types/language'
import { DEFAULT_NAMESPACES, PRODUCERS_NAMESPACES } from 'constants/i18n'
import useWindowSize from 'hooks/useWindowSize'
import { useRouter } from 'next/router'
import type { SeoProps } from 'components/seo/types'
import { useTranslation } from 'next-i18next'
import { cmsLib, producersLib } from 'lib'
import { mapCmsBlogArticleToArticle } from 'utils/cms'

const ProducerDetailPage = (props: ProducerDetailProps) => {
  // Window size
  const { isTablet } = useWindowSize()

  // i18n
  const { t } = useTranslation('producer')

  // Router
  const { locale } = useRouter()

  // Constants
  const { description, name, images } = props.producer
  const SEO: SeoProps = {
    description: description[locale],
    image: images?.profile?.url?.replace('w_800', 'w_1200,h_600,c_thumb'),
    title: t('seoTitle', { name: name[locale] })
  }

  return (
    <Layout seo={SEO}>
      <ProducerDetail isTablet={isTablet} {...props} />
    </Layout>
  )
}

export const getStaticProps = async ({ params, locale }) => {
  const { slug } = params

  const [producer, relatedArticles] = await Promise.all([
    await producersLib.getParentProducerBySlug(slug, { full: true, withExtraImages: true }),
    await cmsLib.getBlogArticles(locale, slug)
  ])

  if (!producer) {
    return {
      notFound: true,
      revalidate: 100
    }
  }

  return {
    props: {
      producer,
      relatedArticle: relatedArticles.length ? mapCmsBlogArticleToArticle(relatedArticles[0]) : null,
      ...(await serverSideTranslations(locale, [...DEFAULT_NAMESPACES, ...PRODUCERS_NAMESPACES]))
    },
    revalidate: 600 // re-generate page when a request comes in, once every 10 minutes
  }
}

export const getStaticPaths = async () => {
  const slugs = await producersLib.getParentProducersSlugs({ language: Language.DUTCH, params: { enabled: true } })
  const paths = []

  // Add both 'nl' and 'fr' path of each existing producer to paths
  slugs.forEach((slug) => {
    const producerPaths = [
      { locale: 'nl', params: { slug } },
      { locale: 'fr', params: { slug } }
    ]
    paths.push(...producerPaths)
  })

  return {
    fallback: 'blocking', // will server-render pages on-demand if the path does not exist
    paths
  }
}

export default ProducerDetailPage
